/**
 * @fileoverview Pipeline view configuration + filter wire codecs (MOR-882)
 *
 * Pure helpers shared by the pipeline surface and its saved-view switcher.
 * Kept out of the component files so react-refresh stays happy and the
 * `PipelineFilterState` <-> server `preAppointmentViewFilters` conversion
 * is independently importable.
 */

import type { Id } from "@cvx/_generated/dataModel";
import type { AppointmentType } from "@cvx/jobs/schema";
import type {
  PreAppointmentViewFilters,
  PreAppointmentViewGroupBy,
  PreAppointmentViewSortBy,
} from "@cvx/preAppointment/preAppointmentViews/schema";

/** Client-side pipeline filter state (string ids for select widgets). */
export interface PipelineFilterState {
  statusIds: string[];
  labelIds: string[];
  referrerIds: string[];
  appointmentTypes: string[];
  includeClosed: boolean;
  search: string;
}

export function emptyPipelineFilters(): PipelineFilterState {
  return {
    statusIds: [],
    labelIds: [],
    referrerIds: [],
    appointmentTypes: [],
    includeClosed: false,
    search: "",
  };
}

/** A pipeline view configuration the switcher saves + restores. */
export interface PipelineViewConfig {
  filters: PipelineFilterState;
  viewType: "board" | "list";
  groupBy: PreAppointmentViewGroupBy;
  sortBy: PreAppointmentViewSortBy;
}

/** Client filter state → server `preAppointmentViewFilters` (drops blanks). */
export function pipelineFiltersToWire(
  f: PipelineFilterState,
): PreAppointmentViewFilters {
  const wire: PreAppointmentViewFilters = {};
  if (f.statusIds.length) {
    wire.statusIds = f.statusIds as Id<"preAppointmentStatuses">[];
  }
  if (f.labelIds.length) {
    wire.labelIds = f.labelIds as Id<"preAppointmentLabels">[];
  }
  if (f.referrerIds.length) {
    wire.referrerFirmContactIds = f.referrerIds as Id<"firmContacts">[];
  }
  if (f.appointmentTypes.length) {
    wire.appointmentTypes = f.appointmentTypes as AppointmentType[];
  }
  if (f.includeClosed) wire.includeClosed = true;
  if (f.search.trim()) wire.search = f.search.trim();
  return wire;
}

/** Server `preAppointmentViewFilters` → client filter state. */
export function pipelineFiltersFromWire(
  w: PreAppointmentViewFilters,
): PipelineFilterState {
  return {
    statusIds: (w.statusIds ?? []).map(String),
    labelIds: (w.labelIds ?? []).map(String),
    referrerIds: (w.referrerFirmContactIds ?? []).map(String),
    appointmentTypes: (w.appointmentTypes ?? []).map(String),
    includeClosed: w.includeClosed ?? false,
    search: w.search ?? "",
  };
}
